import { Link, useLocation } from "react-router";
import { useWorkspace } from "@/api/session";
import { Icon, IconButton, Kbd } from "@/components";
import { BalanceConnect } from "@/devices";
import { useMediaQuery } from "@/lib/hooks";
import { BrandMark } from "./BrandMark";
import { UserMenu } from "./UserMenu";
import styles from "./AppHeader.module.css";

/** Pages with weight fields fed by the balance; the bench pages show their own balance panel. */
const WEIGHING_PAGES = ["/inventory/receive", "/inventory/stocktake", "/lots/"];

export interface AppHeaderProps {
  onOpenPalette: () => void;
  onOpenNavigation: () => void;
}

/** Top bar: navigation toggle (small screens), product name, search/palette trigger, workspace and account. */
export function AppHeader({ onOpenPalette, onOpenNavigation }: AppHeaderProps) {
  const workspace = useWorkspace();
  const compact = useMediaQuery("(width < 48rem)");
  const { pathname } = useLocation();
  const weighsHere = WEIGHING_PAGES.some((prefix) => pathname.startsWith(prefix));
  return (
    <header className={styles.header}>
      <div className={styles.start}>
        <span className={styles.menuButton}>
          <IconButton icon="menu" label="Open navigation" onClick={onOpenNavigation} />
        </span>
        <Link to="/" className={styles.brand} aria-label="Aldehyde home">
          <BrandMark />
          <span className={styles.brandName}>Aldehyde</span>
        </Link>
      </div>
      <div className={styles.center}>
        {compact ? (
          <IconButton icon="search" label="Search or jump to" onClick={onOpenPalette} />
        ) : (
          <button
            type="button"
            className={styles.searchTrigger}
            onClick={onOpenPalette}
            aria-keyshortcuts="Meta+K Control+K"
          >
            <Icon name="search" />
            <span className={styles.searchLabel}>Search or jump to…</span>
            <Kbd keys={["mod", "k"]} />
          </button>
        )}
      </div>
      <div className={styles.end}>
        {weighsHere ? <BalanceConnect size={compact ? "md" : "sm"} compact iconOnly={compact} /> : null}
        {workspace && !compact ? <span className={styles.workspace}>{workspace.name}</span> : null}
        <UserMenu compact={compact} />
      </div>
    </header>
  );
}
